import React, { useState } from "react";
import { CartItemProps } from "./CartItem.types";
import { ProductImage } from "./CartItem.styles";

import {
  Body5MediumTypography,
  Body6RegularTypography,
  Heading5SemiboldTypography,
} from "../../../primitives/Typography/typographyVariants";
import FlexRow from "../../../components/Layout/FlexRow/FlexRow";
import { colorPalette } from "../../../theme/colors";
import FlexColumn from "../../../components/Layout/FlexColumn/FlexColumn";
import { IMAGE_PREFIX } from "../../../utils/constants";
import { ActionButton } from "../../../components/ActionButton/ActionButton";
import { Icon } from "../../../components/Icon/Icon";
import Tags from "../../../components/Tags/Tags";

export const CartItem: React.FC<CartItemProps> = ({
  item,
  variant = "default",
  status = "Delivered",
  collabId,
  onQuantityChange,
  onDelete,
  disabled = false,
  dataCy = "cart-item",
  tagIcon,
  tagTextColor,
  tagBackgroundColor,
  tagIconColor,
  isLastItem = false,
}) => {
  const { id, quantity } = item;
  const { option_name, option_type } = item?.product || {};
  const { mrp, price, thumbnail_url, name, description } =
    item?.product?.product || {};

  const [count, setCount] = useState(quantity || 1);

  const handleIncrement = () => {
    if (disabled) return;

    const newCount = count + 1;
    setCount(newCount);
    onQuantityChange?.(id, newCount);
  };

  const handleDecrement = () => {
    if (disabled) return;

    if (count > 1) {
      const newCount = count - 1;
      setCount(newCount);
      onQuantityChange?.(id, newCount);
    } else {
      onDelete?.(id);
    }
  };
  const sanitize_price = (num: number) => {
    if (!num) {
      return 0;
    }
    const number = parseInt(num.toString());
    return number.toLocaleString();
  };

  const discountPercentage =
    mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  return (
    <FlexRow
      gap={4}
      p={4}
      alignItems="flex-start"
      backgroundColor="#FFF"
      borderBottom={
        !isLastItem ? `1px dashed ${colorPalette.white[100]}` : "none"
      }
      data-cy={dataCy}
    >
      <ProductImage
        src={thumbnail_url || `${IMAGE_PREFIX}/Wishlink-logo.png`}
        alt={`${name} product image`}
        data-cy={`${dataCy}-image`}
      />
      <FlexColumn gap={2} width="unset" flex={1}>
        <Heading5SemiboldTypography
          color={colorPalette.popBlack[500]}
          lineClamp={1}
          overflow="hidden"
          textOverflow="ellipsis"
          textTransform="uppercase"
          maxWidth="150px"
          data-cy={`${dataCy}-name`}
        >
          {name}
        </Heading5SemiboldTypography>
        <Body6RegularTypography
          overflow="hidden"
          textOverflow="ellipsis"
          lineClamp={1}
          maxWidth="150px"
          data-cy={`${dataCy}-description`}
          color={colorPalette.popBlack[500]}
        >
          {description}
        </Body6RegularTypography>
        {option_type && option_name && (
          <FlexRow gap={4}>
            <Body6RegularTypography color={colorPalette.black[100]}>
              {option_type} :
            </Body6RegularTypography>
            <Body6RegularTypography data-cy={`${dataCy}-option`}>
              {option_name}
            </Body6RegularTypography>
          </FlexRow>
        )}
      </FlexColumn>

      {variant === "track" && (
        <Tags
          type="track"
          label={status}
          color={tagTextColor}
          backgroundColor={tagBackgroundColor}
          icon={tagIcon}
          iconColor={tagIconColor}
        />
      )}
      {variant === "default" && (
        <FlexColumn gap={4} width="unset">
          <FlexRow justifyContent="flex-end">
            <ActionButton
              variant="quantity"
              quantity={count}
              onIncrement={handleIncrement}
              onDecrement={handleDecrement}
              disabled={disabled}
              dataCy={`${dataCy}-quantity-control`}
            >
              {count}
            </ActionButton>
          </FlexRow>
          {(mrp || price) && (
            <FlexRow gap={4} alignItems="flex-start" width="unset">
              {mrp && mrp > price && (
                <Body5MediumTypography
                  textDecoration="line-through"
                  color={colorPalette.popBlack[100]}
                  data-cy={`${dataCy}-mrp`}
                >
                  &#8377; {sanitize_price(mrp)}
                </Body5MediumTypography>
              )}
              <Body5MediumTypography color={colorPalette.popBlack[500]}>
                &#8377; {sanitize_price(price)}
              </Body5MediumTypography>
            </FlexRow>
          )}
        </FlexColumn>
      )}
    </FlexRow>
  );
};
